import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { theme } from "../theme.js";
import Grid from "../components/base/Grid.jsx";
import Container from "../components/base/Container.jsx";
import ProfileHeader from "../components/profileHeader.jsx";
import LoadingOverlay from "../components/LoadingOverlay.jsx";
import RaceCard from "../components/raceCard.jsx";
import TransactionTicketCard from "../components/TransactionTicketCard.jsx";
import Section from "../components/base/Section.jsx";
import Input from "../components/base/Input.jsx";
import Radio from "../components/base/Radio.jsx";
import Button from "../components/base/Button.jsx";
import PhoneInput from "../components/base/PhoneInput.jsx";
import { API_URL } from "../utils/constants.js"; 

export default function User() {
  const location = useLocation();
  const [participant, setParticipant] = useState(null);
  const [editedParticipant, setEditedParticipant] = useState({
    name: "",
    firstname: "",
    birthdate: "",
    gender: "",
    email: "",
    phone: "",
    street_number: "",
    postal_code: "",
    city: "",
    country: "",
    emergency_contact: "",
    emergency_phone: "",
  });
  const [followingRaces, setFollowingRaces] = useState([]);
  const [previousRaces, setPreviousRaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(location.state?.tab || "infos");
  const [user, setUser] = useState(null);
  const [raceFilter, setRaceFilter] = useState("all"); // "all", "future", "passed"
  const [transactionFilter, setTransactionFilter] = useState("all"); // "all", "pending", "complete"
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    // Récupérer les informations de l'utilisateur depuis localStorage
    const userData = localStorage.getItem("user");
    const participantData = localStorage.getItem("participant");

    const parsedUser = userData ? JSON.parse(userData) : null;
    setUser(parsedUser);

    const parsedParticipant = participantData ? JSON.parse(participantData) : null;
    setParticipant(parsedParticipant);
    setEditedParticipant(parsedParticipant);
    console.log("Participant data loaded:", parsedParticipant);
    console.log("User data loaded:", parsedUser);

  const fetchUserRaces = async () => {
   try {
      if (!parsedUser?.email) {
        console.warn("No user email found, skipping race fetch");
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/tickets/user/${encodeURIComponent(parsedUser.email)}`,
        { method: "GET",
        headers: { "Content-Type": "application/json" } 
      });

      if (!response.ok) {
        throw new Error("Error fetching user races");
      }

    const data = await response.json();
    console.log("User races fetched:", data);
    setFollowingRaces(data.following || []);
    setPreviousRaces(data.previous || []);
    setLoading(false);
   } 
  catch (err) {
      console.error("Error fetching user races:", err);
      setLoading(false);
  }
}
  fetchUserRaces();
  }, []);

  const handleSaveClick = async () => {
    try {
      console.log("Saving participant:", editedParticipant);
      const response = await fetch(`${API_URL}/api/participants`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editedParticipant),
      });

      if (!response.ok) {
        throw new Error("Error updating participant");
      }

      const updatedData = await response.json();
      setParticipant(updatedData);
      setEditedParticipant(updatedData);
      localStorage.setItem("participant", JSON.stringify(updatedData));
      alert("Informations mises à jour avec succès");
    } catch (error) {
      console.error("Error saving participant:", error);
      alert("Erreur lors de la mise à jour des informations");
    }
  };

  const hasChanges = () => {
    if (!participant || !editedParticipant) return false;
    return JSON.stringify(participant) !== JSON.stringify(editedParticipant);
  };



  return (
    <div style={{ padding: isMobile ? `0 ${theme.spacing.sm}` : `0 ${theme.spacing.xl}` }}>
      <Container>
        <ProfileHeader participant={participant} />

        <div style={styles.tabsContainer}>          
          <div style={styles.tabButtons}>
            <button
              onClick={() => setActiveTab("infos")}
              style={{
                ...styles.tabButton,
                ...(activeTab === "infos" ? styles.tabButtonActive : styles.tabButton),
              }}
            >
              Informations personnelles
            </button>

            <button
              onClick={() => setActiveTab("races")}
              style={{
                ...styles.tabButton,
                ...(activeTab === "races" ? styles.tabButtonActive : styles.tabButton),
              }}
            >
              Mes courses
            </button>
            <button
              onClick={() => setActiveTab("transactions")}
              style={{
                ...styles.tabButton,
                ...(activeTab === "transactions" ? styles.tabButtonActive : styles.tabButton),
              }}
            >
              Transactions
            </button>
          </div>

          <div style={{ ...styles.tabContent, marginLeft: isMobile ? 0 : theme.spacing.huge, width: isMobile ? "100%" : "75%" }}>
            {activeTab === "races" ? (
              (() => {
                const combined = [...followingRaces, ...previousRaces];
                const ownerBibs = combined.filter((bib) => bib?.status === "Purchased");

                // Group by race id to render only one card per race
                const grouped = ownerBibs.reduce((acc, b) => {
                  const rid = b?.race?.id_bdd ?? `race_${b?.race?.id || b?.id_bdd}`;
                  if (!acc[rid]) acc[rid] = [];
                  acc[rid].push(b);
                  return acc;
                }, {});

                let groups = Object.values(grouped);
                
                // Sort by date (descending - most recent first)
                const now = new Date();
                groups.sort((a, b) => {
                  const dateA = new Date(a[0]?.race?.date);
                  const dateB = new Date(b[0]?.race?.date);
                  return dateB - dateA;
                });

                // Filter based on selected filter
                if (raceFilter === "future") {
                  groups = groups.filter((group) => new Date(group[0]?.race?.date) > now);
                } else if (raceFilter === "passed") {
                  groups = groups.filter((group) => new Date(group[0]?.race?.date) <= now);
                }

                return (
                  <>
                    <div style={styles.filterButtonsContainer}>
                      <button
                        onClick={() => setRaceFilter("all")}
                        style={{
                          ...styles.filterButton,
                          ...(raceFilter === "all" ? styles.filterButtonActive : {}),
                        }}
                      >
                        Tous
                      </button>
                      <button
                        onClick={() => setRaceFilter("future")}
                        style={{
                          ...styles.filterButton,
                          ...(raceFilter === "future" ? styles.filterButtonActive : {}),
                        }}
                      >
                        À venir
                      </button>
                      <button
                        onClick={() => setRaceFilter("passed")}
                        style={{
                          ...styles.filterButton,
                          ...(raceFilter === "passed" ? styles.filterButtonActive : {}),
                        }}
                      >
                        Passées
                      </button>
                    </div>
                    {groups.length === 0 ? (
                      <p style={styles.emptyMessage}>Aucune course</p>
                    ) : (
                      <Grid>
                        {groups.map((group) => {
                          const rep = group[0];
                          const isPassed = new Date(rep?.race?.date) <= now;
                          return (
                            <div
                              key={rep?.race?.id_bdd || rep?.id_bdd}
                              style={{
                                opacity: isPassed ? 0.5 : 1,
                                position: "relative",
                              }}
                            >
                              <RaceCard
                                bib={rep}
                                bibs={group}
                              />
                              {isPassed && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    background: "rgba(0,0,0,0.6)",
                                    color: "white",
                                    padding: "8px 12px",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                  }}
                                >
                                  Passée
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </Grid>
                    )}
                  </>
                );
              })()
            ) : null}

            {activeTab === "transactions" ? (
              (() => {
                const combined = [...followingRaces, ...previousRaces];
                const allTransactions = combined.filter((bib) =>
                  ["Sold", "Transferred", "For Sale", "Waiting transfer"].includes(bib?.status)
                );

                // Filter based on transaction status
                const filteredTransactions = allTransactions.filter((bib) => {
                  if (transactionFilter === "pending") {
                    return ["For Sale", "Waiting transfer"].includes(bib?.status);
                  } else if (transactionFilter === "complete") {
                    return ["Sold", "Transferred"].includes(bib?.status);
                  }
                  return true; // "all"
                });

                return (
                  <div>
                    {/* Filter Buttons */}
                    <div style={styles.filterButtonsContainer}>
                      <button
                        onClick={() => setTransactionFilter("all")}
                        style={{
                          ...styles.filterButton,
                          ...(transactionFilter === "all" ? styles.filterButtonActive : {})
                        }}
                      >
                        Tous
                      </button>
                      <button
                        onClick={() => setTransactionFilter("pending")}
                        style={{
                          ...styles.filterButton,
                          ...(transactionFilter === "pending" ? styles.filterButtonActive : {})
                        }}
                      >
                        En attente
                      </button>
                      <button
                        onClick={() => setTransactionFilter("complete")}
                        style={{
                          ...styles.filterButton,
                          ...(transactionFilter === "complete" ? styles.filterButtonActive : {})
                        }}
                      >
                        Complétées
                      </button>
                    </div>

                    {/* Transactions List */}
                    {filteredTransactions.length === 0 ? (
                      <p style={styles.emptyMessage}>Aucune transaction</p>
                    ) : (
                      <div style={styles.transactionsList}>
                        {filteredTransactions.map((ticket) => (
                          <TransactionTicketCard
                            key={ticket?.id_bdd || ticket?.id}
                            ticket={ticket}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()
            ) : null}
            {activeTab === "infos" ? (
              <>
              <Section title="Informations générales">
                <Input 
                  type="text" 
                  label="Nom" 
                  value={editedParticipant?.name || ""} 
                  name="name"
                  setFormData={setEditedParticipant}
                />
                <Input 
                  type="text" 
                  label="Prénom" 
                  value={editedParticipant?.firstname || ""} 
                  name="firstname"
                  setFormData={setEditedParticipant}
                />
                <Input 
                  label="Date de naissance" 
                  value={editedParticipant?.birthdate || ""} 
                  name="birthdate"
                  setFormData={setEditedParticipant}
                  type="date"
                />
                 <Radio
                    choice={{ 
                      name: "gender", 
                      label: "Genre", 
                      choices: [
                        { value: "M", label: "Male" },
                        { value: "F", label: "Female" },
                        { value: "O", label: "Other" }
                      ] 
                    }}
                    formData={editedParticipant}
                    setFormData={setEditedParticipant}
                />
              </Section>
              <Section title="Contact">
                <Input 
                  type="email" 
                  label="Email" 
                  value={editedParticipant?.email || ""} 
                  name="email"
                  setFormData={setEditedParticipant}
                />
                <PhoneInput 
                  label="Téléphone" 
                  phoneValue={editedParticipant?.phone || ""} 
                  name="phone"
                  setFormData={setEditedParticipant}
                />
              </Section>
              <Section title="Adresse">
                <Input 
                  type="text" 
                  label="Adresse" 
                  value={editedParticipant?.street_number || ""} 
                  name="street_number"
                  setFormData={setEditedParticipant}
                />
                <Input 
                  type="text" 
                  label="Code Postal" 
                  value={editedParticipant?.postal_code || ""} 
                  name="postal_code"
                  setFormData={setEditedParticipant}
                />
                <Input 
                  type="text" 
                  label="Ville" 
                  value={editedParticipant?.city || ""} 
                  name="city"
                  setFormData={setEditedParticipant}
                />
                <Input 
                  type="text" 
                  label="Pays" 
                  value={editedParticipant?.country || ""} 
                  name="country"
                  setFormData={setEditedParticipant}
                />
              </Section>
              <Section title="Informations supplémentaires">
                <Input 
                  type="text" 
                  label="Contact d'urgence" 
                  value={editedParticipant?.emergency_contact || ""} 
                  name="emergency_contact"
                  setFormData={setEditedParticipant}
                />
                <PhoneInput 
                  label="Numéro du contact d'urgence" 
                  phoneValue={editedParticipant?.emergency_phone || ""} 
                  name="emergency_phone"
                  setFormData={setEditedParticipant}
                />
              </Section>
                <div style={styles.editButtonContainer}>
                  <Button 
                    title="Sauvegarder les modifications" 
                    onClick={handleSaveClick}
                    disabled={!hasChanges()}
                    style={"dark"}
                  />
                </div>
              </>
            ) : null}
          </div>
        </div>
      </Container>
      <LoadingOverlay isVisible={loading} message="Chargement de vos données..." />
    </div>
  );
}


const styles = {
  emptyMessage: {
    textAlign: "center",
    color: theme.colors.textInverted,
    fontSize: theme.fonts.size.sm,
    padding: `${theme.spacing.xxxl} 0`,
  },
  tabsContainer: {
    marginTop: theme.spacing.lg,
  },
  tabButtons: {
    display: "flex",
    gap: theme.spacing.xs,
    borderBottom: `2px solid ${theme.colors.border}`,
    marginBottom: theme.spacing.lg,
    background: "none",
    flexWrap: "wrap",
  },
  tabButton: {
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    background: "none",
    backgroundColor: "transparent",
    border: "none",
    fontSize: theme.fonts.size.base,
    fontWeight: theme.fonts.weight.medium,
    color: theme.colors.textInverted,
    cursor: "pointer",
    borderBottom: "2px solid transparent",
    marginBottom: -2,
    transition: theme.transitions.smooth,
  },
  tabButtonActive: {
    color: theme.colors.textLight,
    borderBottom: `2px solid ${theme.colors.primary}`,
    backgroundColor: theme.colors.bgDark,
    borderRadius: theme.radius.md,
  },
  tabContent: {
    minHeight: 200,
  },
  editButtonContainer: {
    marginTop: theme.spacing.lg,
    marginBottom: theme.spacing.lg,
      display: "flex",
  },
  buttonGroup: {
    display: "flex",
    gap: theme.spacing.md,
  },
  disabledButton: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  filterButtonsContainer: {
    display: "flex",
    gap: theme.spacing.md,
    marginBottom: theme.spacing.lg,
  },
  filterButton: {
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    background: theme.colors.bgLight,
    color: theme.colors.textInverted,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radius.md,
    cursor: "pointer",
    fontSize: theme.fonts.size.sm,
    fontWeight: theme.fonts.weight.medium,
    transition: theme.transitions.smooth,
  },
  filterButtonActive: {
    background: theme.colors.light,
    color: theme.colors.textInverted,
    borderColor: theme.colors.primary,
  },
  transactionsList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.md,
  },
};
